import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Layout/Header';
import RFPComparisonForm from '../components/RFPComparison/RFPComparisonForm';
import RFPComparisonOutput from '../components/RFPComparison/RFPComparisonOutput';
import { generateRFPComparison } from '../services/rfpComparisonService';
import toast from 'react-hot-toast';

const RFPComparisonPage = () => {
  const [generatedComparison, setGeneratedComparison] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [formData, setFormData] = useState(null);
  const [editableContent, setEditableContent] = useState('');

  const handleGenerate = async (data) => {
    setIsGenerating(true);
    setFormData(data);

    try {
      console.log('Generating RFP comparison with data:', data);
      const result = await generateRFPComparison(data);
      
      setGeneratedComparison(result.comparison);
      setEditableContent(result.comparison);
      
      if (result.isMock) {
        toast.success('RFP comparison generated (demo mode)!');
      } else {
        toast.success('RFP comparison generated successfully!');
      }
    } catch (error) {
      console.error('Generation error:', error);
      
      let errorMessage = error.message || 'Failed to generate RFP comparison. Please try again.';
      if (error.message.includes('API key')) {
        errorMessage = 'API configuration error. Please check your Straico API key.';
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network connection error. Please check your internet connection.';
      } else if (error.message.includes('422')) {
        errorMessage = 'Request format error. The API request format may have changed.';
      } else if (error.message.includes('rate limit')) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      }
      
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClear = () => {
    setGeneratedComparison('');
    setEditableContent('');
    setFormData(null);
  };

  const handleContentChange = (newContent) => {
    setEditableContent(newContent);
  };

  const handleRegenerate = async () => {
    if (formData) {
      await handleGenerate(formData);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            AI RFP Comparison Tool
          </h1>
          <p className="text-gray-600">
            Analyze and compare multiple Request For Proposals with detailed insights and recommendations
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <RFPComparisonForm
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              onClear={handleClear}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <RFPComparisonOutput
              content={editableContent}
              originalContent={generatedComparison}
              isGenerating={isGenerating}
              formData={formData}
              onContentChange={handleContentChange}
              onRegenerate={handleRegenerate}
            />
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default RFPComparisonPage;